import { useGenerationStatus } from '@hooks/apiHooks/generationsHooks';
import { useEffect, useMemo, useRef, useState } from 'react';

interface UsePresentationParams {
  taskId?: string | null;
}

interface UsePresentationResult {
  presentationUrl: string | null;
  isLoadingPresentation: boolean;
  taskId: string | null;
}

export const usePresentation = ({
  taskId,
}: UsePresentationParams): UsePresentationResult => {
  const { data: generationStatus, isLoading } = useGenerationStatus(
    taskId || null,
    !!taskId,
    false,
    false,
  );

  const prevStatusRef = useRef<string | undefined>(undefined);
  const [reloadTimestamp, setReloadTimestamp] = useState<number | null>(null);

  const basePresentationUrl = useMemo(() => {
    return generationStatus?.presentationUrl || null;
  }, [generationStatus?.presentationUrl]);

  useEffect(() => {
    const currentStatus = generationStatus?.status;
    if (
      prevStatusRef.current !== 'completed' &&
      currentStatus === 'completed' &&
      basePresentationUrl
    ) {
      setReloadTimestamp(Date.now());
    }
    prevStatusRef.current = currentStatus;
  }, [generationStatus?.status, basePresentationUrl]);

  const presentationUrl = useMemo(() => {
    if (!basePresentationUrl) return null;
    if (reloadTimestamp) {
      const separator = basePresentationUrl.includes('?') ? '&' : '?';
      return `${basePresentationUrl}${separator}_t=${reloadTimestamp}`;
    }
    return basePresentationUrl;
  }, [basePresentationUrl, reloadTimestamp]);

  const isLoadingPresentation = useMemo(() => {
    if (!taskId) return false;
    if (isLoading) return true;
    if (!generationStatus) return true;
    if (generationStatus.status === 'completed' && basePresentationUrl)
      return false;
    if (generationStatus.status === 'failed') return false;
    return (
      generationStatus.status === 'processing' ||
      generationStatus.status === 'pending'
    );
  }, [taskId, isLoading, generationStatus, basePresentationUrl]);

  return {
    presentationUrl,
    isLoadingPresentation,
    taskId: taskId || null,
  };
};
